import { useState, useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Text, SelectBox, DatePicker, TextEditor, TimePicker, MultiFile } from 'components/input_fields'
import { TicketFormSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import { Loader } from 'components/loader'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { premiseList, zoneList, levelList, unitList } from '../../../../redux/slices/premises'
import { updateTicket, ticketDetails, complaintCategoryList } from '../../../../redux/slices/help_desk'
import { toggleUpdateModal, toggleBool } from '../../../../redux/slices/globalSlice'
import { addUpdateHandler, SessionManager } from 'utils'
import { useTranslation } from 'react-i18next'

const TICKET_FILE_ACCEPT = 'image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip'

const COMPLAINT_TYPE_OPTIONS = [
    { value: 'Personal', label: 'Personal' },
    { value: 'Block Wise', label: 'Block Wise' },
    { value: 'Society Wise', label: 'Society Wise' },
]

const PRIORITY_OPTIONS = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' },
]

export default function UpdateTicketForm() {

    const [loading, setLoading] = useState(true)
    const [breakLoading, setBreakLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const id = useSelector(state => state?.global?.updateId)
    const details = useSelector(state => state?.ticket?.details)
    const premise = useSelector(state => state?.premise?.list)
    const zone = useSelector(state => state?.zone?.list)
    const level = useSelector(state => state?.level?.list)
    const unit = useSelector(state => state?.unit?.list)
    const complaintCategories = useSelector(state => state?.complaintCategory?.list)

    const premiseLoading = useSelector(state => state?.premise?.loading)
    const zoneLoading = useSelector(state => state?.zone?.loading)
    const levelLoading = useSelector(state => state?.level?.loading)
    const unitLoading = useSelector(state => state?.unit?.loading)
    const complaintCategoryLoading = useSelector(state => state?.complaintCategory?.loading)

    const user_data = JSON.parse(SessionManager.getCookie("user"))

    const premise_option = premise?.map(val => ({ value: val?.id, label: val?.name }))
    const zone_option = zone?.map(val => ({ value: val?.id, label: val?.zone_name }))
    const level_option = level?.map(val => ({ value: val?.id, label: val?.level_name }))
    const unit_option = unit?.map(val => ({ value: val?.id, label: val?.unit_number }))
    const complaint_category_option = complaintCategories?.map(val => ({ value: val?.id, label: val?.complaint_category_name }))

    const initialValues = {
        id: details?.id,
        premise_id: details?.premise_id,
        zone_id: details?.zone_id,
        level_id: details?.level_id,
        unit_id: details?.unit_id,
        complaint_category: details?.complaint_category_id,
        complaint_type: details?.incident_type,
        priority: details?.incident_priority,
        subject: details?.incident_title,
        description: details?.incident_remark,
        incident_date: details?.incident_date && moment(details.incident_date).format('DD/MM/YYYY'),
        incident_time: details?.incident_time || '',
        attachments: [],
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues,
        validationSchema: TicketFormSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(updateTicket({ values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('ticket.ticket')} ${t('common.success_update')}`,
                wrong_toast: t('common.wrong'),
                navigate_route: '',
                spinner: setSpinner,
                toggle: () => {
                    dispatch(toggleUpdateModal())
                    dispatch(toggleBool())
                }
            }))
        },
    })

    const details_res = async (res) => {
        try {
            if (res?.status === 200) {
                const data = res?.data?.data
                await Promise.all([
                    dispatch(premiseList({ navigate })),
                    dispatch(complaintCategoryList({ navigate })),
                    dispatch(zoneList({ pageable: { premise_id: data?.premise_id }, navigate })),
                    dispatch(levelList({ pageable: { zone_id: data?.zone_id }, navigate })),
                    dispatch(unitList({ pageable: { level_id: data?.level_id }, navigate })),
                ])
                setLoading(false)
                setBreakLoading(false)
            } else {
                setBreakLoading(false)
            }
        } catch {
            setBreakLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true)
        setBreakLoading(true)
        dispatch(ticketDetails({ id, navigate })).then((res) => details_res(res.payload))
    }, [])

    const onPremiseChange = (premiseVal) => {
        setFieldValue('premise_id', premiseVal)
        setFieldValue('zone_id', '')
        setFieldValue('level_id', '')
        setFieldValue('unit_id', '')
        if (premiseVal) {
            dispatch(zoneList({ pageable: { premise_id: premiseVal }, navigate }))
        }
    }

    const onZoneChange = (zoneVal) => {
        setFieldValue('zone_id', zoneVal)
        setFieldValue('level_id', '')
        setFieldValue('unit_id', '')
        if (zoneVal) {
            dispatch(levelList({ pageable: { zone_id: zoneVal }, navigate }))
        }
    }

    const onLevelChange = (levelVal) => {
        setFieldValue('level_id', levelVal)
        setFieldValue('unit_id', '')
        if (levelVal) {
            dispatch(unitList({ pageable: { level_id: levelVal }, navigate }))
        }
    }

    if (loading && breakLoading) return <Loader />
    if (loading && !breakLoading) return (
        <div className='layout'>
            <div className='text-center'>
                <h5>Something went wrong, can&apos;t load update form</h5>
            </div>
        </div>
    )

    return (
        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className='container-fluid'>
                <div className='row'>

                    <div className='col-12 mt-1 mb-2'>
                        <div className='section_header mb-0'>
                            <div className='section_title_wrapper'>
                                <h6 className='section_title'>{t('ticket.location_details')}</h6>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.premise')}
                            mandatory={true}
                            placeholder=''
                            disabled={user_data?.premise_id}
                            loading={premiseLoading}
                            option={premise_option}
                            name='premise_id'
                            value={values.premise_id}
                            onChange={(e) => onPremiseChange(e)}
                            onBlur={() => setFieldTouched('premise_id', true)}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            error={errors.premise_id && touched.premise_id ? <span className='field_error'>{errors.premise_id}</span> : null}
                        />
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.zone')}
                            mandatory={false}
                            placeholder=''
                            disabled={!values.premise_id}
                            loading={zoneLoading}
                            option={zone_option}
                            name='zone_id'
                            value={values.zone_id}
                            onChange={(e) => onZoneChange(e)}
                            onClear={true}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.level')}
                            mandatory={false}
                            placeholder=''
                            disabled={!values.zone_id}
                            loading={levelLoading}
                            option={level_option}
                            name='level_id'
                            value={values.level_id}
                            onChange={(e) => onLevelChange(e)}
                            onClear={true}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.unit')}
                            mandatory={false}
                            placeholder=''
                            disabled={!values.level_id}
                            loading={unitLoading}
                            option={unit_option}
                            name='unit_id'
                            value={values.unit_id}
                            onChange={(e) => setFieldValue('unit_id', e)}
                            onClear={true}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                    </div>

                    <div className='col-12 mt-2 mb-2'>
                        <div className='section_header mb-0'>
                            <div className='section_title_wrapper'>
                                <h6 className='section_title'>{t('ticket.complaint_details')}</h6>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.complaint_category')}
                            mandatory={true}
                            placeholder=''
                            loading={complaintCategoryLoading}
                            option={complaint_category_option}
                            name='complaint_category'
                            value={values.complaint_category}
                            onChange={(e) => setFieldValue('complaint_category', e)}
                            onBlur={() => setFieldTouched('complaint_category', true)}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            error={errors.complaint_category && touched.complaint_category ? <span className='field_error'>{errors.complaint_category}</span> : null}
                        />
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.complaint_type')}
                            mandatory={true}
                            placeholder=''
                            option={COMPLAINT_TYPE_OPTIONS}
                            name='complaint_type'
                            value={values.complaint_type}
                            onChange={(e) => setFieldValue('complaint_type', e)}
                            onBlur={() => setFieldTouched('complaint_type', true)}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            error={errors.complaint_type && touched.complaint_type ? <span className='field_error'>{errors.complaint_type}</span> : null}
                        />
                    </div>

                    <div className='col-md-3'>
                        <SelectBox
                            label_name={t('ticket.priority')}
                            mandatory={true}
                            placeholder=''
                            option={PRIORITY_OPTIONS}
                            name='priority'
                            value={values.priority}
                            onChange={(e) => setFieldValue('priority', e)}
                            onBlur={() => setFieldTouched('priority', true)}
                            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                            error={errors.priority && touched.priority ? <span className='field_error'>{errors.priority}</span> : null}
                        />
                    </div>

                    <div className='col-12 mt-2 mb-2'>
                        <div className='section_header mb-0'>
                            <div className='section_title_wrapper'>
                                <h6 className='section_title'>{t('ticket.issue_information')}</h6>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-12'>
                        <Text
                            label_name={t('ticket.subject')}
                            mandatory={true}
                            placeholder=''
                            name='subject'
                            value={values.subject}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.subject && touched.subject ? <span className='field_error'>{errors.subject}</span> : null}
                        />
                    </div>

                    <div className='col-md-12'>
                        <TextEditor
                            label_name={t('ticket.description')}
                            mandatory={true}
                            value={values.description}
                            onChange={(html) => setFieldValue('description', html)}
                            onBlur={() => setFieldTouched('description', true)}
                            error={errors.description && touched.description ? <span className='field_error'>{errors.description}</span> : null}
                        />
                    </div>

                    <div className='col-12 mt-2 mb-2'>
                        <div className='section_header mb-0'>
                            <div className='section_title_wrapper'>
                                <h6 className='section_title'>{t('ticket.incident_details')}</h6>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-6'>
                        <DatePicker
                            label_name={t('ticket.incident_date')}
                            mandatory={true}
                            placeholder=''
                            name='incident_date'
                            value={values.incident_date}
                            onChange={(date, dateString) => setFieldValue('incident_date', dateString)}
                            onBlur={() => setFieldTouched('incident_date', true)}
                            error={errors.incident_date && touched.incident_date ? <span className='field_error'>{errors.incident_date}</span> : null}
                        />
                    </div>

                    <div className='col-md-6'>
                        <TimePicker
                            label_name={t('ticket.incident_time')}
                            mandatory={false}
                            placeholder=''
                            name='incident_time'
                            value={values.incident_time}
                            onChange={(time, timeString) => setFieldValue('incident_time', timeString || '')}
                            onBlur={() => setFieldTouched('incident_time', true)}
                        />
                    </div>

                    <div className='col-md-12'>
                        <MultiFile
                            label_name={t('ticket.attachments')}
                            mandatory={false}
                            name='attachments'
                            accept={TICKET_FILE_ACCEPT}
                            onChange={(e) => setFieldValue('attachments', Array.from(e.currentTarget.files))}
                            onBlur={handleBlur}
                        />
                    </div>

                    <div className='col-md-12 mt-4'>
                        <div className='d-flex justify-content-end'>
                            <div className='col-lg-4'>
                                <SaveButton button_name={t('common.submit')} spinner={spinner} />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Form>
    )
}